import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as VerifiedIcon,
  Cancel as UnverifiedIcon,
  Refresh as RefreshIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  Pinterest as PinterestIcon,
  Share as ShareIcon,
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon
} from '@mui/icons-material';
import api from '../../utils/api';

// Platform icons mapping
const platformIcons = {
  instagram: <InstagramIcon sx={{ color: '#E4405F' }} />,
  linkedin: <LinkedInIcon sx={{ color: '#0A66C2' }} />,
  youtube: <YouTubeIcon sx={{ color: '#FF0000' }} />,
  twitter: <TwitterIcon sx={{ color: '#1DA1F2' }} />,
  pinterest: <PinterestIcon sx={{ color: '#E60023' }} />,
  tiktok: <ShareIcon sx={{ color: '#000000' }} />
};

const MyAccounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Edit dialog
  const [editDialog, setEditDialog] = useState({ open: false, account: null });
  const [formData, setFormData] = useState({
    account_name: '',
    account_handle: '',
    api_key: '',
    api_secret: '',
    access_token: '',
    refresh_token: '',
    bearer_token: '',
    notes: ''
  });

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, account: null });

  // Show token states
  const [showTokens, setShowTokens] = useState({});

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/social-media/accounts');
      setAccounts(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (account) => {
    setFormData({
      account_name: account.account_name || '',
      account_handle: account.account_handle || '',
      api_key: '', // Don't prefill sensitive data
      api_secret: '',
      access_token: '',
      refresh_token: '',
      bearer_token: '',
      notes: account.notes || ''
    });
    setEditDialog({ open: true, account });
  };

  const handleCloseEdit = () => {
    setEditDialog({ open: false, account: null });
    setFormData({
      account_name: '',
      account_handle: '',
      api_key: '',
      api_secret: '',
      access_token: '',
      refresh_token: '',
      bearer_token: '',
      notes: ''
    });
  };

  const handleUpdate = async () => {
    try {
      // Only send non-empty fields for update
      const updateData = {};
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          updateData[key] = formData[key];
        }
      });

      await api.put(`/social-media/accounts/${editDialog.account.id}`, updateData);
      setSuccess('Account updated successfully');
      handleCloseEdit();
      fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update account');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/social-media/accounts/${deleteDialog.account.id}`);
      setSuccess('Account deleted successfully');
      setDeleteDialog({ open: false, account: null });
      fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete account');
    }
  };

  const handleVerify = async (account) => {
    try {
      const response = await api.post(`/social-media/accounts/${account.id}/verify`);
      if (response.data.success) {
        setSuccess('Account verified successfully');
        fetchAccounts();
      } else {
        setError(response.data.message || 'Verification failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed');
    }
  };

  const getPlatformIcon = (platformCode) => {
    return platformIcons[platformCode?.toLowerCase()] || <ShareIcon />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'expired': return 'error';
      case 'revoked': return 'error';
      default: return 'default';
    }
  };

  const toggleShowTokens = (accountId) => {
    setShowTokens(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const filteredAccounts = accounts.filter(account =>
    account.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.account_handle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.platform?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/digital-media/social-registration')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              My Social Media Accounts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your registered social media accounts
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/digital-media/social-registration')}
        >
          Register New Account
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          placeholder="Search accounts..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
      </Paper>

      {accounts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ShareIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No Accounts Registered</Typography>
          <Typography color="text.secondary" gutterBottom>
            Start by registering your social media accounts to enable posting.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/digital-media/social-registration')}
            sx={{ mt: 2 }}
          >
            Register Account
          </Button>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Platform</TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Handle</TableCell>
                  <TableCell>Verified</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Token Expires</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <TableRow key={account.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getPlatformIcon(account.platform?.platform_code)}
                        {account.platform?.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={500}>{account.account_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color="text.secondary">
                        {account.account_handle || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {account.is_verified ? (
                        <Tooltip title={`Verified on ${new Date(account.verification_date).toLocaleDateString()}`}>
                          <VerifiedIcon color="success" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Not verified">
                          <UnverifiedIcon color="disabled" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={account.status} 
                        size="small" 
                        color={getStatusColor(account.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {account.token_expires_at ? (
                        <Typography 
                          variant="body2"
                          color={new Date(account.token_expires_at) < new Date() ? 'error' : 'text.secondary'}
                        >
                          {new Date(account.token_expires_at).toLocaleDateString()}
                        </Typography>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {account.last_used_at ? (
                        <Typography variant="body2" color="text.secondary">
                          {new Date(account.last_used_at).toLocaleString()}
                        </Typography>
                      ) : 'Never'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Verify Connection">
                        <IconButton 
                          size="small"
                          onClick={() => handleVerify(account)}
                          color="primary"
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small"
                          onClick={() => handleOpenEdit(account)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, account })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {editDialog.account && getPlatformIcon(editDialog.account.platform?.platform_code)}
            Edit {editDialog.account?.platform?.name} Account
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Leave credential fields empty to keep existing values.
            </Alert>

            <TextField
              label="Account Name"
              required
              fullWidth
              value={formData.account_name}
              onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
            />

            <TextField
              label="Account Handle"
              fullWidth
              value={formData.account_handle}
              onChange={(e) => setFormData({ ...formData, account_handle: e.target.value })}
            />

            <Divider sx={{ my: 1 }}>
              <Chip label="Update Credentials (optional)" size="small" />
            </Divider>

            <TextField
              label="API Key"
              fullWidth
              value={formData.api_key}
              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
              placeholder="Leave empty to keep existing"
            />

            <TextField
              label="API Secret"
              fullWidth
              type="password"
              value={formData.api_secret}
              onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
              placeholder="Leave empty to keep existing"
            />

            <TextField
              label="Access Token"
              fullWidth
              type="password"
              value={formData.access_token}
              onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
              placeholder="Leave empty to keep existing"
            />

            <TextField
              label="Refresh Token"
              fullWidth
              type="password"
              value={formData.refresh_token}
              onChange={(e) => setFormData({ ...formData, refresh_token: e.target.value })}
              placeholder="Leave empty to keep existing"
            />

            <TextField
              label="Bearer Token"
              fullWidth
              type="password"
              value={formData.bearer_token}
              onChange={(e) => setFormData({ ...formData, bearer_token: e.target.value })}
              placeholder="Leave empty to keep existing"
            />

            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleUpdate}
            disabled={!formData.account_name}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, account: null })}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.account?.account_name}"? 
            This will remove all stored credentials and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, account: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyAccounts;
